import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const REQUEST_TYPES = [
    {
        value: "access",
        label: "View my data",
        body: "Send me a copy of the personal information you hold about me.",
    },
    {
        value: "correct",
        label: "Correct my data",
        body: "Update information that's inaccurate (for example, the wrong phone number or city).",
    },
    {
        value: "delete",
        label: "Delete my data",
        body: "Permanently remove my information from your records.",
    },
    {
        value: "withdraw",
        label: "Withdraw consent",
        body: "End the matching service and stop processing my information from now on.",
    },
];

export default function PrivacyRequestPage() {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const [requestType, setRequestType] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const payload = Object.fromEntries(fd.entries());
        if (!payload.email || !payload.request_type) {
            toast.error("Please enter your email and choose a request type.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/privacy/requests", payload);
            setSubmitted(data);
            toast.success("Request received. Check your inbox for a confirmation.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="bg-brand-paper min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="privacy-request-success">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-xl text-center"
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Request received
                    </div>
                    <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink">
                        We've got <span className="italic">it</span>.
                    </h1>
                    <p className="mt-8 text-lg text-brand-muted leading-relaxed">
                        Your data request has been logged. We aim to respond within 7 business days, and
                        under PIPEDA we have up to 30 days. A confirmation has also been sent to your
                        email.
                    </p>
                    <p className="mt-6 text-sm text-brand-muted">
                        Reference: <code className="font-mono text-brand-ink">{submitted.id}</code>
                    </p>
                    <Link
                        to="/"
                        className="mt-10 inline-flex items-center gap-2 text-sm tracking-wide text-brand-ink hover:text-black"
                    >
                        Back to home <span aria-hidden>→</span>
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="bg-brand-paper" data-testid="privacy-request-page">
            <section className="mx-auto max-w-3xl px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Manage your data
                    </div>
                    <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight text-brand-ink">
                        Your data, <span className="italic">your call</span>
                    </h1>
                    <p className="mt-8 text-lg text-brand-muted leading-relaxed">
                        Use this form to view, correct, delete, or withdraw consent over the personal
                        information we hold about you under PIPEDA. We aim to respond within 7 business
                        days and have up to 30 days by law.
                    </p>
                    <p className="mt-4 text-sm text-brand-muted">
                        See also our{" "}
                        <Link to="/privacy-policy" className="text-brand-ink underline">
                            Privacy Policy
                        </Link>{" "}
                        for the full details.
                    </p>
                </motion.div>
            </section>

            <section className="mx-auto max-w-3xl px-6 lg:px-10 pb-24 lg:pb-32">
                <form
                    onSubmit={onSubmit}
                    className="border border-brand-line bg-brand-paper p-8 lg:p-12 space-y-10"
                    data-testid="privacy-request-form"
                >
                    <div className="space-y-5">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold">
                            1 · Your email
                        </div>
                        <p className="text-sm text-brand-muted">
                            Use the email address we have on file. We'll verify your identity by replying
                            to that address before releasing or changing any information.
                        </p>
                        <label className="block">
                            <span className="block text-sm text-brand-muted mb-2">Email *</span>
                            <input
                                type="email"
                                name="email"
                                required
                                data-testid="privacy-email"
                                className="w-full bg-transparent border-b border-brand-line py-2 text-brand-ink focus:outline-none focus:border-brand-ink transition-colors"
                            />
                        </label>
                    </div>

                    <div className="space-y-5">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold">
                            2 · What would you like to do?
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {REQUEST_TYPES.map((t) => {
                                const selected = requestType === t.value;
                                return (
                                    <label
                                        key={t.value}
                                        data-testid={`privacy-type-${t.value}`}
                                        className={`block cursor-pointer border p-5 transition-colors ${
                                            selected
                                                ? "border-brand-ink bg-brand-sand/60"
                                                : "border-brand-line hover:border-brand-ink/40"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="request_type"
                                            value={t.value}
                                            checked={selected}
                                            onChange={() => setRequestType(t.value)}
                                            className="sr-only"
                                            required
                                        />
                                        <div className="flex items-center gap-3 mb-2">
                                            <div
                                                className={`w-3 h-3 rounded-full border ${
                                                    selected ? "bg-brand-ink border-brand-ink" : "border-brand-line"
                                                }`}
                                            />
                                            <div className="font-serif text-lg text-brand-ink">{t.label}</div>
                                        </div>
                                        <p className="text-sm text-brand-muted leading-relaxed">{t.body}</p>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold">
                            3 · Anything else? (optional)
                        </div>
                        <label className="block">
                            <span className="block text-sm text-brand-muted mb-2">
                                Details — for a correction, please tell us exactly what to change.
                            </span>
                            <textarea
                                name="details"
                                rows={4}
                                data-testid="privacy-details"
                                className="w-full bg-transparent border border-brand-line p-3 text-brand-ink focus:outline-none focus:border-brand-ink transition-colors resize-y"
                            />
                        </label>
                    </div>

                    <div className="pt-2 flex flex-wrap gap-4 items-center">
                        <button
                            type="submit"
                            disabled={submitting || !requestType}
                            data-testid="privacy-submit-button"
                            className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-8 py-4 text-sm tracking-wide hover:bg-black transition-all hover:gap-4 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Submitting…" : "Submit request"}
                            {!submitting && <span aria-hidden>→</span>}
                        </button>
                        <p className="text-xs text-brand-muted max-w-md">
                            We'll confirm by email. No data will be released or changed without verifying
                            your identity through the email address on file.
                        </p>
                    </div>
                </form>
            </section>
        </div>
    );
}
